import React, {
  useState,
  useMemo,
  useCallback,
  useLayoutEffect,
  useEffect,
} from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Typewriter from "typewriter-effect";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import TextField from "../../components/TextField/Login";
import { useAuth } from "../../hooks/useAuth";
import { SignInCredentials } from "../../types/session";
import { useStyles } from "./styles";
import { useTheme, FormControlLabel, Checkbox } from "@mui/material";
import { useCustomStorage } from "../../hooks/useCustomStorage";
import { usePortal } from "../../hooks/usePortal";
import axios from "axios";
import stage from "../../utils/stage";

type FormValues = {
  email: string;
  password: string;
};

const phrases = [
  "Venda mais no WhatsApp hoje mesmo!",
  "Anúncios que atraem clientes certos!",
  "Redes sociais sempre atualizadas!",
  "Seu concorrente está um passo à frente?",
  "Alcance mais clientes com tráfego pago!",
  "Conteúdo diário para engajar seu público!",
  "Monitore e supere seus concorrentes!",
  "Transforme conversas em vendas no WhatsApp!",
  "Posts profissionais sem esforço!",
  "Mais visibilidade, mais vendas, mais lucro!",
];

const validationSchema = Yup.object().shape({
  email: Yup.string().required("Digite seu e-mail").email("Email inválido"),
  password: Yup.string().required("Digite sua senha"),
});

const Login: React.FC = () => {
  const hostname = window.location.hostname;
  const styles = useStyles();
  const navigate = useNavigate();
  const { signIn, signInSmart } = useAuth();
  const storage = useCustomStorage();
  const theme = useTheme();
  const { isMobile } = usePortal();
  const logo = storage.getItem("LOGO");

  const [hidePassword, setHidePassword] = useState<boolean>(true);
  const [financialError, setFinancialError] = useState<string | null>(null);
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);

  const handleSignIn = useCallback(
    async (values: SignInCredentials) => {
      try {
        setFinancialError(null);

        if (
          hostname.toLowerCase().includes("smart") ||
          hostname.toLowerCase().includes("pas") ||
          hostname.toLowerCase().includes("bombeef") ||
          hostname.toLowerCase().includes("linkedby") ||
          hostname.toLowerCase().includes("adnotify")
        ) {
          await signInSmart({
            ...values,
            host: hostname.toLowerCase().includes("smart")
              ? "smart"
              : hostname.toLowerCase().includes("bombeef")
              ? "bombeef"
              : hostname.toLowerCase().includes("linkedby") ||
                hostname.toLowerCase().includes("adnotify")
              ? "linkedby"
              : "pas",
          });
        } else {
          await signIn(values);
        }
        location.reload();
        setFinancialError(null);
      } catch (err: any) {
        if (err.response?.status === 502) {
          setFinancialError(
            "Não foi possível acessar a plataforma. Tente novamente"
          );
        } else if (err.response?.status === 402) {
          setFinancialError("Não foi possível encontrar o acesso");
        } else if (err.response?.status === 400) {
          const message =
            err.response?.data?.error || "Não foi possível encontrar o acesso";

          setFinancialError(message);
        } else {
          const message =
            err.response?.data?.message === "Senha não corresponde"
              ? "Não foi possível encontrar o acesso"
              : hostname.toLowerCase().includes("smart") ||
                hostname.toLowerCase().includes("pas") ||
                hostname.toLowerCase().includes("boombeef")
              ? "Não foi possível encontrar o acesso"
              : "Seu acesso foi temporariamente desativado por pendências financeiras. Regularize seus débitos ou fale com um de nossos atendentes.";
          setFinancialError(message);
        }
      }
    },
    [signIn, signInSmart, hostname]
  );

  useEffect(() => {
    const logosDefault = async () => {
      const params = {
        id: hostname.toLowerCase().includes("smart")
          ? "smart"
          : hostname.toLowerCase().includes("bombeef")
          ? "bombeef"
          : hostname.toLowerCase().includes("linkedby") ||
            hostname.toLowerCase().includes("adnotify")
          ? "linkedby"
          : hostname.toLowerCase().includes("pas")
          ? "pas"
          : hostname.toLowerCase().includes("martins")
          ? "martins"
          : "app",
      };

      const response = await axios.get(
        `https://api-backoffice.datasales.info/studio-${stage}/distributors/${params.id}`,
        { params }
      );

      const data = response.data.content;
      storage.setItem("LOGO", data?.logo_url);
      storage.setItem("LOGO_FULL", data?.logo_full_url);
      storage.setItem("LOGO_HEADER", data?.header_url);
      storage.setItem("ISDISTRIBUTION", String(Boolean(data?.isDistribution)));
      storage.setItem("DISTRIBUTORID", String(data?.distributor_id));
      storage.setItem("NAME", data?.name);
      storage.setItem("NEEDTRAINING", String(Boolean(data?.needTraining)));
      storage.setItem("NEEDVIDEOS", String(Boolean(data?.needVideos)));
      storage.setItem("SUPPORT", data?.support_number);
      storage.setItem("NEEDWHATSAPP", String(Boolean(data?.needWhatsApp)));
    };

    logosDefault();
  }, []);

  const formik = useFormik<FormValues>({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: handleSignIn,
  });

  const typeInputPassword = useMemo(() => {
    if (hidePassword) {
      return "password";
    }
    return undefined;
  }, [hidePassword]);

  const distributorIsDsMarketing = useMemo(
    () =>
      !hostname.toLowerCase().includes("smart") ||
      !hostname.toLowerCase().includes("pas") ||
      !hostname.toLowerCase().includes("bombeef") ||
      !hostname.toLowerCase().includes("linkedby") ||
      !hostname.toLowerCase().includes("adnotify"),
    [hostname]
  );

  useLayoutEffect(() => {
    if (hostname.toLowerCase().includes("smart")) {
      storage.setItem("@datasales:smart", "true".toString());
    }

    if (hostname.toLowerCase().includes("pas")) {
      storage.setItem("@datasales:martins", "true".toString());
    }

    if (hostname.toLowerCase().includes("bombeef")) {
      storage.setItem("@datasales:bombeef", "true".toString());
    }

    if (
      hostname.toLowerCase().includes("linkedby") ||
      hostname.toLowerCase().includes("adnotify")
    ) {
      storage.setItem("@datasales:linkedby", "true".toString());
    }
  }, [hostname]);

  useEffect(() => {
    let __checked = Boolean(localStorage.getItem("@persist:keepLoggedIn"));
    __checked = __checked
      ? JSON.parse(localStorage.getItem("@persist:keepLoggedIn")!)
      : false;

    setKeepLoggedIn(__checked);
  }, []);

  const handleSupportClick = () => {
    window.parent.postMessage(["toogle"], "*");
  };

  const handleCheckboxChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setKeepLoggedIn(checked);
    localStorage.setItem("@persist:keepLoggedIn", String(checked));
  };

  return (
    <Grid
      container
      spacing={0}
      margin={0}
      sx={{
        height: "100vh",
        justifyContent: isMobile ? "center" : "normal",
        bgcolor: isMobile ? "#0C20B5" : undefined,
      }}
    >
      <Grid
        item
        xs={8}
        px={5}
        sx={{
          bgcolor: "#FFF",
          display: isMobile ? "none" : "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{ fontSize: "54pt", lineHeight: "72pt", textAlign: "center" }}
        >
          <Typewriter
            options={{
              strings: phrases,
              autoStart: true,
              loop: true,
              delay: 100, // Velocidade da digitação
              deleteSpeed: 50, // Velocidade ao apagar
            }}
          />
        </Typography>
      </Grid>
      <Grid
        sx={{ bgcolor: distributorIsDsMarketing ? "#0C20B5" : "#f2f2f2" }}
        item
        xs={isMobile ? 9 : 4}
      >
        <Stack
          spacing={2}
          height="100vh"
          alignItems="center"
          justifyContent="center"
          px={isMobile ? 0 : "5vw"}
        >
          <img
            width={
              hostname.toLowerCase().includes("smart")
                ? 250
                : hostname.toLowerCase().includes("pas")
                ? 200
                : hostname.toLowerCase().includes("linkedby") ||
                  hostname.toLowerCase().includes("adnotify")
                ? 250
                : isMobile
                ? 60
                : 120
            }
            alt=""
            src={
              logo
                ? logo
                : hostname.toLowerCase().includes("smart")
                ? import.meta.env.VITE_REACT_LOGO_SMART
                : hostname.toLowerCase().includes("bombeef")
                ? import.meta.env.VITE_REACT_LOGO_BOMBEEF_FULL
                : hostname.toLowerCase().includes("linkedby") ||
                  hostname.toLowerCase().includes("adnotify")
                ? import.meta.env.VITE_REACT_LOGO_LINKEDBY
                : hostname.toLowerCase().includes("pas")
                ? import.meta.env.VITE_REACT_LOGO_MARTINS
                : import.meta.env.VITE_REACT_LOGO_DS_2
            }
          />
          <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
            <Stack spacing={2} width="100%">
              <Stack spacing={1} width="100%">
                <Typography
                  color={distributorIsDsMarketing ? "#FFF" : undefined}
                  variant="button"
                >
                  Email
                </Typography>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  onChange={formik.handleChange("email")}
                  error={Boolean(formik.errors.email)}
                  helperText={formik.errors.email}
                />
              </Stack>
              <Stack spacing={1} width="100%">
                <Typography
                  color={distributorIsDsMarketing ? "#FFF" : undefined}
                  variant="button"
                >
                  Senha
                </Typography>
                <TextField
                  id="password"
                  fullWidth
                  autoComplete="current-password"
                  type={typeInputPassword}
                  name="password"
                  onChange={formik.handleChange("password")}
                  error={Boolean(formik.errors.password)}
                  helperText={formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          sx={{ color: "#FFF" }}
                          onClick={() => setHidePassword((prev) => !prev)}
                        >
                          {hidePassword ? (
                            <i className="fi fi-rr-eye" />
                          ) : (
                            <i className="fi fi-rr-eye-crossed" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Stack spacing={2} width="100%">
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ padding: 0, bgcolor: "#FFF" }}
                      checked={keepLoggedIn}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={
                    <Typography
                      pl={1}
                      color={distributorIsDsMarketing ? "#FFF" : undefined}
                      variant="button"
                    >
                      Manter-me conectado
                    </Typography>
                  }
                />
              </Stack>
              {financialError && (
                <Stack
                  spacing={2}
                  width="100%"
                  px={2}
                  py={1}
                  bgcolor="#B02F2F"
                  borderRadius="8px"
                >
                  <Typography variant="caption" color="#FFFFFF">
                    {financialError}
                    {financialError === "Não foi possível encontrar o acesso"}
                  </Typography>
                  {financialError !== "Senha não corresponde" && (
                    <Button
                      test-id="appbar-button-support"
                      variant="contained"
                      style={{
                        backgroundColor: "#EDF0F4",
                        color: "#B02F2F",
                      }}
                      onClick={handleSupportClick}
                    >
                      Falar com suporte
                    </Button>
                  )}
                </Stack>
              )}
              <Button
                sx={{
                  color: distributorIsDsMarketing
                    ? theme.palette.primary.main
                    : undefined,
                  bgcolor: distributorIsDsMarketing ? "#FFF" : undefined,
                  "&:hover": {
                    color: distributorIsDsMarketing
                      ? theme.palette.primary.main
                      : undefined,
                    bgcolor: distributorIsDsMarketing ? "#FFF" : undefined,
                  },
                }}
                variant="contained"
                fullWidth
                disabled={formik.isSubmitting}
                type="submit"
              >
                {formik.isSubmitting ? (
                  <CircularProgress size={24} className={styles.progress} />
                ) : (
                  "Entrar"
                )}
              </Button>
            </Stack>
          </form>
          <>
            <Typography
              color={distributorIsDsMarketing ? "#FFF" : undefined}
              variant="overline"
            >
              ou
            </Typography>
            <Button
              sx={{
                color: distributorIsDsMarketing ? "#FFF" : undefined,
                borderColor: distributorIsDsMarketing ? "#FFF" : undefined,
                "&:hover": {
                  color: distributorIsDsMarketing ? "#FFF" : undefined,
                  borderColor: distributorIsDsMarketing ? "#FFF" : undefined,
                },
              }}
              onClick={() => navigate("/signup")}
              className={styles.retrieveAccount}
              fullWidth
              variant="outlined"
            >
              Cadastrar gratuitamente
            </Button>
          </>
          <Stack>
            <Button
              sx={{ color: distributorIsDsMarketing ? "#FFF" : undefined }}
              onClick={() => navigate("/account-recover")}
              className={styles.retrieveAccount}
              size="small"
              fullWidth
              variant="text"
            >
              Esqueceu a senha?
            </Button>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default Login;
